import React, {useRef, Suspense, useEffect } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber'

const fragmentShader = `
uniform float iTime;
uniform vec2 iResolution;

float rand2dTo1d(vec2 value, vec2 dotDir){
    vec2 smallValue = sin(value);
    float random = dot(smallValue, dotDir);
    random = fract(sin(random) * 143758.5453);   
    return random;
}

float rand1dTo1d(float value, float mutator){
	float random = fract(sin(value + mutator) * 143758.5453);
	return random;
}

vec2 rand2dTo2d(vec2 value){
    return vec2(
        rand2dTo1d(value, vec2(12.989, 78.233)),
        rand2dTo1d(value, vec2(39.346, 11.135))
    );
}

float easeIn(float interpolator){
    return interpolator * interpolator;
}

float easeOut(float interpolator){
	return 1.0 - easeIn(1.0 - interpolator);
}

float easeInOut(float interpolator){
	float easeInValue = easeIn(interpolator);
	float easeOutValue = easeOut(interpolator);
	return mix(easeInValue, easeOutValue, interpolator);
}

float perlinNoise2D(vec2 uv, vec2 scale, vec2 offset){
  uv.y = 1.0 - uv.y;
  uv *= scale;
  uv += offset;
  vec2 fraction = fract(uv);
	vec2 lowerLeftDirection = rand2dTo2d(vec2(floor(uv.x), floor(uv.y))) * 2.0 - 1.0;
	vec2 lowerRightDirection = rand2dTo2d(vec2(ceil(uv.x), floor(uv.y))) * 2.0 - 1.0;
	vec2 upperLeftDirection = rand2dTo2d(vec2(floor(uv.x), ceil(uv.y))) * 2.0 - 1.0;
	vec2 upperRightDirection = rand2dTo2d(vec2(ceil(uv.x), ceil(uv.y))) * 2.0 - 1.0;
	float lowerLeftFunctionValue = dot(lowerLeftDirection, fraction - vec2(0, 0));
	float lowerRightFunctionValue = dot(lowerRightDirection, fraction - vec2(1, 0));
	float upperLeftFunctionValue = dot(upperLeftDirection, fraction - vec2(0, 1));
	float upperRightFunctionValue = dot(upperRightDirection, fraction - vec2(1, 1));
	float interpolatorX = easeInOut(fraction.x);
	float interpolatorY = easeInOut(fraction.y);
	float lowerCells = mix(lowerLeftFunctionValue, lowerRightFunctionValue, interpolatorX);
	float upperCells = mix(upperLeftFunctionValue, upperRightFunctionValue, interpolatorX);
	float noise = mix(lowerCells, upperCells, interpolatorY);
	return noise + 0.5;
}

vec3 palette(float t){
    vec3 a = vec3(0.610, 0.498, 0.650);
    vec3 b = vec3(0.388, 0.498, 0.350);
    vec3 c = vec3(0.530, 0.498, 0.620);
    vec3 d = vec3(3.438, 3.012, 4.025);

    return a + b*cos(6.28318*(c*t+d));
}

float segment ( vec2 p, vec2 a, vec2 b ) {
    float h; b = b-a; a = p-a;
    h = clamp( dot(a,b)/dot(b,b), 0.0, 1.0 );
    return 1.5-distance( a, h*(b*b) );
}

float lemniscate ( vec2 p) {
    p = abs(p); float a = 0.0, b = 1.0;
    float H1 = p.x+p.y; float H3 = 3.0*H1;
    float H2 = p.x-p.y; float H4 = 3.0*H2;
    for ( int i = 0; i < 6; i ++ ) {
        float t = (a+b)/2.0;
        float E = t*(t*(t*t*(t*(t*H2-2.0)+H3)-H4)+2.0)-H1;
        ( E < 0.0 ) ? a = t : b = t;
    }; float u = a*a*a, v = b*b*b;
    vec2 q1 = vec2(a+u,a-u)/(a*u+1.0);
    vec2 q2 = vec2(b+v,b-v)/(b*v+1.0);
    return segment(p,q1,q2);
}

void main()
{   
    vec2 uv = (gl_FragCoord.xy - iResolution.xy*vec2(0.85,0.55))/iResolution.x;
    vec3 finalColor = vec3(0.0);
    float noiseMap1 = perlinNoise2D(uv,vec2(1000.0),vec2(1.0)) -0.5;
    float anim = sin(iTime + 1.75);

    for(float i = 1.0; i < 5.0; i++)
    {
        uv.x *= 1.0 + ((i-1.0 ) * 0.02) * noiseMap1;
        uv.y *= 1.0 + ((i-1.0 ) * 0.02) * noiseMap1;

        float dist = lemniscate(uv * 3.5 + (uv * (i * anim * 0.2)) ) * 500.0;
        vec3 col = palette(i + sin(iTime * 3.0 + length(uv) * 3.0));
        dist = sin(dist*0.02 + iTime) / 0.02;
        dist -= sin(dist*0.02 + -iTime + 0.9) / 0.02;
        dist = abs(dist);
        dist = dist + (1.0-dist) * 0.8;
        dist = 0.9/dist/i;
        finalColor += col * (dist / i / 0.5 );
    }

    gl_FragColor = vec4(finalColor,1.0); 
}
`;

const Wave = (props) => {

  const materialRef = useRef();

  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.iTime.value = clock.getElapsedTime() * 0.25;
    }
  });

  useEffect(() => {
    function handleResize() {
      if (materialRef.current) {
        materialRef.current.uniforms.iResolution.value = new THREE.Vector3(
       window.innerWidth * window.devicePixelRatio,
        window.innerHeight * window.devicePixelRatio,
          1
        );
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  return (
  <mesh>
    <planeBufferGeometry args={[1, 1, 1, 1]} />
      <shaderMaterial
        ref={materialRef}
        fragmentShader={fragmentShader}
        uniforms={{
          iTime: { value: 0 },
          iResolution: { value: new THREE.Vector3(
       window.innerWidth * window.devicePixelRatio,
        window.innerHeight * window.devicePixelRatio,
            1
          ) 
        }
      }}
    />
  </mesh>
  )

}

const Scene = () => {
  return (
    <div>
      <div className='test'>
        <Canvas className='' camera={{ fov: 1, rotation: [0, 0, 0], position: [0, 0, 1] }}>
          <Suspense fallback={null}>
            <Wave />
          </Suspense>
        </Canvas>
      </div>
      <div className='vignette'></div>
    </div>
  )
}

const App = () => {
  return <Scene />
}

export default App;